import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Tooltip } from '../Tooltip';

type ContextualHelpPosition = 'top' | 'bottom' | 'left' | 'right';
type ContextualHelpVariant = 'info' | 'question' | 'light';

export type ContextualHelpProps = {
  content: ReactNode;
  position?: ContextualHelpPosition;
  variant?: ContextualHelpVariant;
  size?: 'sm' | 'md' | 'lg';
  delay?: number;
  className?: string;
  icon?: ReactNode;
};

const InfoIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z"
      clipRule="evenodd"
    />
  </svg>
);

const QuestionIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0ZM8.94 6.94a.75.75 0 1 1-1.061-1.061 3 3 0 1 1 2.871 5.026v.345a.75.75 0 0 1-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 1 0 8.94 6.94ZM10 15a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
      clipRule="evenodd"
    />
  </svg>
);

const LightIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path d="M10 1a6 6 0 0 0-3.815 10.631C7.237 12.5 8 13.443 8 14.456v.644a.75.75 0 0 0 .75.75h2.5a.75.75 0 0 0 .75-.75v-.644c0-1.013.762-1.957 1.815-2.825A6 6 0 0 0 10 1ZM8.863 17.414a.75.75 0 0 0-.226 1.483 9.066 9.066 0 0 0 2.726 0 .75.75 0 0 0-.226-1.483 7.553 7.553 0 0 1-2.274 0Z" />
  </svg>
);

const sizeStyles = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
};

const variantStyles = {
  info: 'text-info hover:text-info-darker',
  question: 'text-gray-400 hover:text-gray-500 dark:text-dark-400 dark:hover:text-dark-300',
  light: 'text-warning hover:text-warning-darker',
};

function ContextualHelp({
  content,
  position = 'top',
  variant = 'info',
  size = 'md',
  delay = 0,
  className,
  icon,
}: ContextualHelpProps) {
  const IconComponent =
    variant === 'question' ? QuestionIcon : variant === 'light' ? LightIcon : InfoIcon;

  return (
    <Tooltip content={content} position={position} delay={delay}>
      <button
        type="button"
        className={clsx(
          'inline-flex items-center justify-center transition-colors cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-full',
          variantStyles[variant],
          className
        )}
        aria-label="Help information"
      >
        {icon || <IconComponent className={sizeStyles[size]} />}
      </button>
    </Tooltip>
  );
}

ContextualHelp.displayName = 'ContextualHelp';

export { ContextualHelp };
